import { Dispatch, useContext } from 'react';

import {
  ApolloQueryResult,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';

import { LocalizationContext } from 'localization';
import {
  CREATE_GOOD,
  DELETE_GOOD,
  DELETE_GOODS,
  EXPORT_GOODS,
  GOODS,
  UPDATE_GOOD,
  UPDATE_GOODS,
} from 'schemas';
import notification from 'services/notificationService';
import { TGood } from 'types';

import {
  ICreateGoodArgs,
  IUpdateGoodArgs,
  IUpdateGoodsArgs,
  TCreateGood,
  TDeleteGood,
  TDeleteGoods,
  TUpdateGood,
  TUpdateGoods,
} from './interfaces';

export const useGoodsRequests = ({
  title_search,
  category_id,
  setIsDeletingError,
  setDeletingMessage,
}: {
  title_search?: string;
  category_id?: string;
  setIsDeletingError?: (value: boolean) => void;
  setDeletingMessage?: Dispatch<React.SetStateAction<string>>;
}): {
  updateGood: (args: IUpdateGoodArgs) => Promise<unknown>;
  isGoodsLoading: boolean;
  loadedGoods: TGood[];
  createGood: (args: ICreateGoodArgs) => Promise<unknown>;
  deleteGood: (id: number) => Promise<unknown>;
  toggleShowInShell: (args: IUpdateGoodArgs) => Promise<unknown>;
  exportGoods: (
    searchStr?: string,
    groupFiltered?: string | null | 0
  ) => Promise<unknown>;
  deleteGoods: (ids: number[]) => Promise<unknown>;
  updateGoods: (args: IUpdateGoodsArgs) => Promise<unknown>;
  refetchGoods: (
    variables?: Partial<{
      input: { title_search?: string; category_id?: string | null | 0 };
    }>
  ) => Promise<ApolloQueryResult<{ goods: Array<TGood> }>>;
} => {
  const { t } = useContext(LocalizationContext);

  const { data, loading, refetch } = useQuery<{ goods: Array<TGood> }>(GOODS, {
    variables: {
      input: {
        title_search: title_search || undefined,
        category_id: category_id || undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [exportGoodsRequest] = useLazyQuery<{
    exportGoods: string;
  }>(EXPORT_GOODS, {
    variables: {
      input: {
        title_search: title_search || undefined,
        category_id: category_id || undefined,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const exportGoods = async (
    titleSearch?: string,
    categoryId?: string | null | 0
  ): Promise<void> => {
    try {
      const response = await exportGoodsRequest({
        variables: {
          input: {
            title_search: titleSearch || undefined,
            category_id: categoryId !== 'empty' ? categoryId : undefined,
          },
        },
      });
      if (!response.data?.exportGoods) throw new Error('no data');
      window.open(response.data.exportGoods);
    } catch {
      notification.info({
        t,
        type: 'error',
        title: t('goods.export.error'),
      });
    }
  };

  const [createGoodRequest] = useMutation(CREATE_GOOD, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: () => {
      notification.info({
        t,
        type: 'success',
        title: t('good.created'),
        dismiss: 5,
      });
    },
  });
  const createGood: TCreateGood = ({
    title,
    cost,
    wholesale_cost,
    amount,
    use_global_discounts,
    comment,
    use_fair_sign,
    eans,
    NDS,
    is_excise,
    show_in_shell,
    image,
    category,
    low_stock_notification,
  }) =>
    createGoodRequest({
      variables: {
        input: {
          title,
          cost,
          eans,
          show_in_shell,
          wholesale_cost: wholesale_cost || 0,
          use_fair_sign,
          use_global_discounts,
          is_excise,
          ...(amount ? { amount } : {}),
          ...(comment ? { comment } : {}),
          ...(NDS ? { vat: NDS } : {}),
          image,
          category_id: category?.id || null,
          low_stock_notification: low_stock_notification || null,
        },
      },
    });

  const [updateGoodRequest] = useMutation(UPDATE_GOOD, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: () =>
      notification.info({
        t,
        type: 'success',
        title: t('good.updated'),
        dismiss: 5,
      }),
    // onError: ({ message }) => {
    //   let errorMessage = '';
    //
    //   switch (message) {
    //     case 'good is used in combo':
    //       errorMessage = t('good.combo.error');
    //       break;
    //     case 'ean is already taken':
    //       errorMessage = t('errors.ean.inUse');
    //       break;
    //   }
    //
    //   return notification.info({
    //     t,
    //     type: 'error',
    //     title: t('good.update.error'),
    //     message: errorMessage,
    //     dismiss: 5,
    //   });
    // },
  });
  const updateGood: TUpdateGood = ({
    id,
    title,
    cost,
    wholesale_cost,
    use_global_discounts,
    comment,
    use_fair_sign,
    eans,
    NDS,
    is_excise,
    show_in_shell,
    image,
    category,
    low_stock_notification,
    highlighted,
  }) =>
    updateGoodRequest({
      variables: {
        id,
        input: {
          title,
          cost,
          wholesale_cost: wholesale_cost || 0,
          use_global_discounts,
          use_fair_sign,
          eans,
          is_excise,
          ...(comment ? { comment } : {}),
          ...(NDS ? { vat: NDS } : {}),
          show_in_shell,
          image,
          category_id: category?.id || null,
          low_stock_notification: low_stock_notification || null,
          highlighted,
        },
      },
    });

  const [updateGoodsRequest] = useMutation(UPDATE_GOODS, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: () =>
      notification.info({
        t,
        type: 'success',
        title: t('goods.updated'),
        dismiss: 5,
      }),
    onError: () => {
      notification.info({
        t,
        type: 'error',
        title: t('good.update.error'),
        message: t('good.combo.error'),
        dismiss: 5,
      });
    },
  });

  const updateGoods: TUpdateGoods = ({ ids, input }) =>
    updateGoodsRequest({
      variables: {
        ids,
        input: input,
      },
    });

  const [toggleShowInShellRequest] = useMutation(UPDATE_GOOD, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: (data) => {
      const showInShell = data?.updateGood?.show_in_shell;
      const title = data?.updateGood?.title;

      notification.info({
        t,
        type: 'success',
        title: t('group.table.show_in_shell.title'),
        message: showInShell
          ? `«${title}» ${t('goods.update.show_in_shell.yes')}`
          : `«${title}» ${t('goods.update.show_in_shell.no')}`,
        dismiss: 5,
      });
    },
    onError: ({ message }) => {
      let errorMessage = '';

      switch (message) {
        case 'good quantity is not zero':
          errorMessage = t('good.delete.errors.warehouse');
          break;
        case 'good is used in combo':
          errorMessage = t('good.combo.error');
          break;
      }
      return notification.info({
        t,
        type: 'error',
        title: t('good.update.error'),
        message: errorMessage,
        dismiss: 5,
      });
    },
  });

  const toggleShowInShell: TUpdateGood = async ({
    id,
    title,
    cost,
    wholesale_cost,
    use_global_discounts,
    comment,
    use_fair_sign,
    eans,
    NDS,
    is_excise,
    show_in_shell,
    image,
    category,
    low_stock_notification,
  }): Promise<void> => {
    try {
      await toggleShowInShellRequest({
        variables: {
          id,
          input: {
            title,
            cost,
            wholesale_cost: wholesale_cost || 0,
            use_global_discounts,
            use_fair_sign,
            eans,
            is_excise,
            ...(comment ? { comment } : {}),
            ...(NDS ? { vat: NDS } : {}),
            show_in_shell,
            image,
            category_id: category?.id || null,
            low_stock_notification,
          },
        },
      });
    } catch (error) {
      console.error('Ошибка при обновлении товара:', error);
    }
  };

  const [deleteGoodRequest] = useMutation(DELETE_GOOD, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: ({ deleteGood }) =>
      notification.info({
        t,
        type: 'success',
        title: t('good.deleted'),
        message: deleteGood.title,
        dismiss: 5,
      }),
    onError: ({ message }) => {
      let errorMessage = '';
      let errorMessageModal = '';

      switch (message) {
        case 'good quantity is not zero':
          errorMessage = t('good.delete.errors.warehouse');
          errorMessageModal = t('goods.error.delete.modal');
          break;
        case 'good is used in combo':
          errorMessage = t('good.combo.error');
          errorMessageModal = t('goods.error.delete.modal_2');
          break;
      }
      setDeletingMessage && setDeletingMessage(errorMessageModal);
      setIsDeletingError?.(true);
      return notification.info({
        t,
        type: 'error',
        title: t('good.delete.errors.title'),
        message: errorMessage,
        dismiss: 5,
      });
    },
  });

  const deleteGood: TDeleteGood = (id) =>
    deleteGoodRequest({ variables: { id } });

  const [deleteGoodsRequest] = useMutation(DELETE_GOODS, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GOODS],
    onCompleted: () =>
      notification.info({
        t,
        type: 'success',
        title: t('goods.deleted'),
        dismiss: 5,
      }),
    onError: ({ message }) => {
      let errorMessage = '';
      let errorMessageModal = '';

      switch (message) {
        case 'good quantity is not zero':
          errorMessage = t('good.delete.errors.warehouse');
          errorMessageModal = t('goods.error.delete.modal');
          break;
        case 'good is used in combo':
          errorMessage = t('good.combo.error');
          errorMessageModal = t('goods.error.delete.modal_2');
          break;
      }

      setDeletingMessage && setDeletingMessage(errorMessageModal);
      setIsDeletingError?.(true);

      return notification.info({
        t,
        type: 'error',
        title: t('good.delete.errors.title'),
        message: errorMessage,
        dismiss: 5,
      });
    },
  });

  const deleteGoods: TDeleteGoods = (ids) =>
    deleteGoodsRequest({ variables: { ids } });

  return {
    loadedGoods: data?.goods ?? [],
    isGoodsLoading: loading,
    createGood,
    updateGood,
    deleteGood,
    toggleShowInShell,
    exportGoods,
    deleteGoods,
    updateGoods,
    refetchGoods: refetch,
  };
};
